import { twMerge } from "tailwind-merge";
import type { Config } from "tailwindcss";
import animate from "tailwindcss-animate";

import { MEDIA_THUMBNAIL_SIZE } from "@perceptron/constants";

export default {
  darkMode: "class",
  content: [
    "./pages/**/*.{js,ts,jsx,tsx,mdx}",
    "./components/**/*.{js,ts,jsx,tsx,mdx}",
    "./app/**/*.{js,ts,jsx,tsx,mdx}",
    "./src/**/*.{js,ts,jsx,tsx,mdx}",
    "../../packages/ui/src/**/*.{js,ts,jsx,tsx,mdx}",
    "../../packages/core/src/**/*.{js,ts,jsx,tsx,mdx}",
  ],
  theme: {
    extend: {
      screens: {
        "3xl": "1920px",
        "4xl": "2560px",
        "5xl": "3840px",
      },
      outlineColor: {
        ring: "var(--ring)",
      },
      borderColor: {
        border: "var(--border)",
      },
      width: {
        "media-thumbnail": `${MEDIA_THUMBNAIL_SIZE}px`,
      },
      height: {
        "media-thumbnail": `${MEDIA_THUMBNAIL_SIZE}px`,
      },
      boxShadow: {
        small: "var(--small-shadow)",
        big: "var(--big-shadow)",
      },
      colors: {
        /** Core Colors */
        blue: {
          DEFAULT: "var(--blue)",
          faded: "var(--blue-faded)",
        },
        orange: "var(--orange)",
        teal: "var(--teal)",
        red: "var(--red)",
        success: "var(--success)",
        error: "var(--error)",
        brand: {
          primary: "var(--brand-primary)",
          blue: {
            DEFAULT: "var(--brand-blue)",
            hover: "var(--brand-blue-hover)",
          },
        },
        selection: {
          background: "var(--selection-background)",
        },

        /** Background Colors */
        background: {
          DEFAULT: "var(--background)",
          primary: {
            DEFAULT: "var(--background)",
            inverse: "var(--background-primary-inverse)",
          },
          hover: "var(--background-hover)",
          alt: {
            DEFAULT: "var(--background-alt-1)",
            1: "var(--background-alt-1)",
            "2": "var(--background-alt-2)",
            inverse: {
              DEFAULT: "var(--background-alt-inverse-1)",
              1: "var(--background-alt-inverse-1)",
            },
          },
          accent: "var(--background-accent)",
          error: "var(--background-error)",
          muted: "var(--background-muted)",
          tint: "var(--background-tint)",
        },

        /** Foreground Colors */
        foreground: {
          DEFAULT: "var(--foreground)",
          primary: {
            DEFAULT: "var(--foreground)",
            inverse: "var(--foreground-primary-inverse)",
          },
          secondary: {
            DEFAULT: "var(--foreground-secondary)",
            inverse: "var(--foreground-secondary-inverse)",
          },
          disabled: {
            DEFAULT: "var(--foreground-disabled)",
            inverse: "var(--foreground-disabled-inverse)",
          },
        },

        /** Badge Colors */
        badge: "var(--badge)",

        /** Card Colors */
        card: {
          DEFAULT: "var(--card)",
          foreground: "var(--card-foreground)",
        },

        /** Popover Colors */
        popover: {
          DEFAULT: "var(--popover)",
          foreground: "var(--popover-foreground)",
        },

        /** Primary Colors */
        primary: {
          DEFAULT: "var(--primary)",
          foreground: "var(--primary-foreground)",
        },

        /** Secondary Colors */
        secondary: {
          DEFAULT: "var(--secondary)",
          foreground: "var(--secondary-foreground)",
        },

        /** Muted Colors */
        muted: {
          DEFAULT: "var(--muted)",
          foreground: "var(--muted-foreground)",
        },

        /** Accent Colors */
        accent: {
          DEFAULT: "var(--accent)",
          foreground: "var(--accent-foreground)",
        },

        /** Destructive Colors */
        destructive: {
          DEFAULT: "var(--destructive)",
          foreground: "var(--destructive-foreground)",
        },

        /** Input Colors */
        input: "var(--input)",

        /** Ring Colors */
        ring: "var(--ring)",

        /** Skeleton Colors */
        skeleton: "var(--skeleton)",

        /** Chart Colors */
        chart: {
          DEFAULT: "var(--chart-1)",
          1: "var(--chart-1)",
          2: "var(--chart-2)",
          3: "var(--chart-3)",
          4: "var(--chart-4)",
          5: "var(--chart-5)",
        },

        /** Sidebar Colors */
        sidebar: {
          DEFAULT: "var(--sidebar)",
          foreground: "var(--sidebar-foreground)",
          primary: {
            DEFAULT: "var(--sidebar-primary)",
            foreground: "var(--sidebar-primary-foreground)",
          },
          accent: {
            DEFAULT: "var(--sidebar-accent)",
            foreground: "var(--sidebar-accent-foreground)",
          },
          border: "var(--sidebar-border)",
          ring: "var(--sidebar-ring)",
          hover: "var(--sidebar-hover)",
          active: {
            DEFAULT: "var(--sidebar-active)",
            secondary: "var(--sidebar-active-secondary)",
          },
        },

        /** Annotation toolbar button colors */
        toolbar: {
          button: {
            DEFAULT: "var(--toolbar-button)",
            hover: "var(--toolbar-button-hover)",
          },
        },

        /** Switch Colors */
        switch: {
          unchecked: "var(--switch-unchecked)",
          uncheckedHover: "var(--switch-unchecked-hover)",
          checked: "var(--switch-checked)",
          checkedHover: "var(--switch-checked-hover)",
        },
      },
      fontFamily: {
        sans: ["var(--font-inter)", "ui-sans-serif", "system-ui", "sans-serif"],
        ppmondwest: ["var(--font-ppmondwest)", "ui-sans-serif", "system-ui", "sans-serif"],
        inter: ["var(--font-inter)", "Inter", "ui-sans-serif", "system-ui", "sans-serif"],
        mono: ["Monaco", "Consolas", "Courier New", "monospace"],
      },
      borderRadius: {
        DEFAULT: "var(--radius-lg)", // 10px
        sm: "var(--radius-sm)", // 4px
        md: "var(--radius-md)", // 8px
        lg: "var(--radius-lg)", // 10px
        xl: "var(--radius-xl)", // 12px
        "2xl": "var(--radius-2xl)", // 16px
      },
      keyframes: {
        "fade-in": {
          "0%": { opacity: "0" },
          "100%": { opacity: "1" },
        },
        "fade-out": {
          "0%": { opacity: "1" },
          "100%": { opacity: "0" },
        },
        "fade-in-left": {
          "0%": { opacity: "0", transform: "translateX(100%)" },
          "100%": { opacity: "1", transform: "translateX(0)" },
        },
        "fade-out-left": {
          "0%": { opacity: "1", transform: "translateX(0)" },
          "100%": { opacity: "0", transform: "translateX(-100%)" },
        },
        "pulse-glow": {
          "0%": { opacity: "0.4" },
          "100%": { opacity: "0" },
        },
        "slide-in-right": {
          "0%": { transform: "translateX(100%)", opacity: "0" },
          "100%": { transform: "translateX(0)", opacity: "1" },
        },
        blink: {
          "0%, 50%": { opacity: "1" },
          "51%, 100%": { opacity: "0" },
        },
        "zoom-label-fade-in": {
          "0%": { opacity: "0" },
          "100%": { opacity: "1" },
        },
        "zoom-clamp-in-tl": {
          "0%": { transform: "translate(-16px, -16px)", opacity: "0" },
          "100%": { transform: "translate(0, 0)", opacity: "1" },
        },
        "zoom-clamp-in-tr": {
          "0%": { transform: "translate(16px, -16px)", opacity: "0" },
          "100%": { transform: "translate(0, 0)", opacity: "1" },
        },
        "zoom-clamp-in-bl": {
          "0%": { transform: "translate(-16px, 16px)", opacity: "0" },
          "100%": { transform: "translate(0, 0)", opacity: "1" },
        },
        "zoom-clamp-in-br": {
          "0%": { transform: "translate(16px, 16px)", opacity: "0" },
          "100%": { transform: "translate(0, 0)", opacity: "1" },
        },
      },
      animation: {
        "fade-in": "fade-in 0.3s ease-in-out",
        "zoom-label-fade-in": "zoom-label-fade-in 1s cubic-bezier(0.36,0.04,0.00,0.99) 0.6s both",
        "zoom-clamp-in-tl": "zoom-clamp-in-tl 0.5s cubic-bezier(0.36,0.04,0.00,0.99) 0.35s both",
        "zoom-clamp-in-tr": "zoom-clamp-in-tr 0.5s cubic-bezier(0.36,0.04,0.00,0.99) 0.35s both",
        "zoom-clamp-in-bl": "zoom-clamp-in-bl 0.5s cubic-bezier(0.36,0.04,0.00,0.99) 0.35s both",
        "zoom-clamp-in-br": "zoom-clamp-in-br 0.5s cubic-bezier(0.36,0.04,0.00,0.99) 0.35s both",
        "pulse-glow": "pulse-glow 1.8s cubic-bezier(0.4, 0, 0.6, 1) infinite",
        "slide-in-right": "slide-in-right 0.3s ease-in-out",
        blink: "blink 1s infinite",
      },
      variants: {
        extend: {
          opacity: ["group-hover", "image-container-hover"],
        },
      },
    },
  },
  plugins: [
    animate,
    ({ addUtilities }: { addUtilities: (utilities: Record<string, any>) => void }) => {
      addUtilities({
        ".display-1": {
          fontSize: "var(--display-1-font-size)",
          fontWeight: "var(--display-1-font-weight)",
          lineHeight: "var(--display-1-line-height)",
        },
        ".display-2": {
          fontSize: "var(--display-2-font-size)",
          fontWeight: "var(--display-2-font-weight)",
          lineHeight: "var(--display-2-line-height)",
        },
        ".display-3": {
          fontSize: "var(--display-3-font-size)",
          fontWeight: "var(--display-3-font-weight)",
          lineHeight: "var(--display-3-line-height)",
        },
        ".title-1": {
          fontSize: "var(--title-1-font-size)",
          fontWeight: "var(--title-1-font-weight)",
          lineHeight: "var(--title-1-line-height)",
        },
        ".title-2": {
          fontSize: "var(--title-2-font-size)",
          fontWeight: "var(--title-2-font-weight)",
          lineHeight: "var(--title-2-line-height)",
        },
        ".title-3": {
          fontSize: "var(--title-3-font-size)",
          fontWeight: "var(--title-3-font-weight)",
          lineHeight: "var(--title-3-line-height)",
        },
        ".title-4": {
          fontSize: "var(--title-4-font-size)",
          fontWeight: "var(--title-4-font-weight)",
          lineHeight: "var(--title-4-line-height)",
        },
        ".headline": {
          fontSize: "var(--headline-font-size)",
          fontWeight: "var(--headline-font-weight)",
          lineHeight: "var(--headline-line-height)",
        },
        ".body": {
          fontSize: "var(--body-font-size)",
          fontWeight: "var(--body-font-weight)",
          lineHeight: "var(--body-line-height)",
        },
        ".label-1": {
          fontSize: "var(--label-1-font-size)",
          fontWeight: "var(--label-1-font-weight)",
          lineHeight: "var(--label-1-line-height)",
        },
        ".label-2": {
          fontSize: "var(--label-2-font-size)",
          fontWeight: "var(--label-2-font-weight)",
          lineHeight: "var(--label-2-line-height)",
        },
        ".caption": {
          fontSize: "var(--caption-font-size)",
          fontWeight: "var(--caption-font-weight)",
          lineHeight: "var(--caption-line-height)",
          textTransform: "var(--caption-text-transform)",
        },
        ".legal": {
          fontSize: "var(--legal-font-size)",
          fontWeight: "var(--legal-font-weight)",
          lineHeight: "var(--legal-line-height)",
        },
      });
    },
  ],
} satisfies Config;

export type { Config };

export { twMerge };
